import { ColDef, ICellRendererParams } from 'ag-grid-community';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { crudStatusEnum, getCrudStatusLabel, localStorageKeys, permissionLevelEnum } from '../../../../constants';
import { getCrudStatusColor } from '../../../../constants/colors';
import { useUpdateTruckCompanyMutation } from '../../../../hooks/mutations/useTruckCompanyMutation';
import { useTrucksCompaniesQuery } from '../../../../hooks/queries/useTrucksCompaniesQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { TruckCompany } from '../../../../services/apiSLTP/materials/trucksCompanies';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { createDateTimeColumn } from '../../../../utils/tables/commonColumns';
import StateCell from '../../../cells/StateCell';
import UpdateTruckCompanyModal from '../../../modals/admin/external/UpdateTruckCompanyModal';
import DeleteModal from '../../../modals/DeleteModal';
import IconButton from '../../../ui/IconButton';
import IconLine from '../../../ui/IconLine';
import GenericAgGridTable from '../../GenericAgGridTable';

interface TrucksCompaniesSettingsRowProps {
    withUsageCount: boolean;
}

const TrucksCompaniesSettingsRow: React.FC<TrucksCompaniesSettingsRowProps> = ({ withUsageCount }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedTruckCompany, setSelectedTruckCompany] = useState<TruckCompany | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);

    const {
        data: dataTruckCompanies,
        isLoading: isTruckCompaniesLoading,
        refetch: refetchTruckCompanies,
    } = useTrucksCompaniesQuery({ queries: { withUsage: withUsageCount } });

    const {
        mutate: mutateTruckCompany,
        isSuccess: isSuccessTruckCompany,
        isError: isErrorTruckCompany,
        isLoading: isLoadingTruckCompany,
        error: errorTruckCompany,
        data: dataTruckCompany,
    } = useUpdateTruckCompanyMutation({
        onSuccess: (data, variables) => {
            refetchTruckCompanies();
        },
        onError: (error) => {
            console.error('Error updating truckCompany:', error.message);
        },
    });

    const columns: ColDef<TruckCompany>[] = [
        { headerName: capitalizeFirstLetter(t('name')), field: 'designation', initialFlex: 1, minWidth: 400, colId: 'name' },
        {
            headerName: capitalizeFirstLetter(t('actions')),
            cellRenderer: (params: ICellRendererParams<TruckCompany>) => {
                if (!params.data) return null;
                return (
                    <IconLine>
                        <IconButton
                            iconName="edit"
                            title={capitalizeFirstLetter(t('modifyTruckCompany'))}
                            onClick={() => {
                                if (!params.data) return;
                                setSelectedTruckCompany(params.data);
                                setIsEditMode(true);
                                setShowEditModal(true);
                            }}
                            disabled={params.data.crudStatus === crudStatusEnum.DELETED}
                        />
                        {user && user.profileId.permsLevel <= permissionLevelEnum.ADMIN && (
                            <IconButton
                                iconName="delete"
                                title={capitalizeFirstLetter(t('deleteTruckCompany'))}
                                onClick={() => {
                                    if (!params.data) return;
                                    setSelectedTruckCompany(params.data);
                                    setShowDeleteModal(true);
                                }}
                                disabled={params.data.crudStatus === crudStatusEnum.DELETED}
                            />
                        )}
                    </IconLine>
                );
            },
            colId: 'actions',
            filter: false,
            enableRowGroup: false,
            enableValue: false,
            sortable: false,
        },
        {
            headerName: capitalizeFirstLetter(t('status')),
            valueGetter: (params) => capitalizeFirstLetter(t(getCrudStatusLabel(params.data?.crudStatus))),
            cellRenderer: (params: ICellRendererParams<TruckCompany>) => {
                if (!params.data) return params.value;
                return <StateCell stateColor={getCrudStatusColor(params.data?.crudStatus)} text={t(getCrudStatusLabel(params.data?.crudStatus))} />;
            },
            colId: 'status',
        },
        { headerName: capitalizeFirstLetter(t('email')), field: 'email', colId: 'email' },
        {
            headerName: capitalizeFirstLetter(t('isOwnCompany')),
            valueGetter: (params) => !!params.data?.isOwnCompany,
            colId: 'isOwnCompany',
            cellDataType: 'boolean',
        },
        createDateTimeColumn<TruckCompany>('createdAt', 'createdAt'),
        createDateTimeColumn<TruckCompany>('updatedAt', 'updatedAt'),
        ...((user?.profileId.permsLevel === permissionLevelEnum.ADMIN
            ? [
                  { headerName: capitalizeFirstLetter(t('_id')), field: '_id', colId: 'xtotal', cellClass: 'usage-col', headerClass: 'usage-col' },
                  {
                      headerName: capitalizeFirstLetter(t('xTotal')),
                      field: 'usageCount.total',
                      initialWidth: 250,
                      colId: 'xtotal',
                      cellClass: 'usage-col-main',
                      headerClass: 'usage-col-main',
                  },
                  {
                      headerName: capitalizeFirstLetter(t('xRidesPlanned')),
                      field: 'usageCount.dailyTrucksPlanned',
                      colId: 'xridesplanned',
                      cellClass: 'usage-col',
                      headerClass: 'usage-col',
                      filter: 'agNumberColumnFilter',
                  },
                  {
                      headerName: capitalizeFirstLetter(t('xTrucks')),
                      field: 'usageCount.trucks',
                      colId: 'xtrucks',
                      cellClass: 'usage-col',
                      headerClass: 'usage-col',
                      filter: 'agNumberColumnFilter',
                  },
              ]
            : []) as ColDef<TruckCompany>[]),
    ];

    const sortedTruckCompanies = useMemo(() => {
        return dataTruckCompanies?.sort((a, b) => a.designation.localeCompare(b.designation)) || [];
    }, [dataTruckCompanies]);

    return (
        <>
            <GenericAgGridTable
                columnDefs={columns}
                rowData={sortedTruckCompanies.filter((item) => item.crudStatus !== crudStatusEnum.DELETED)}
                loading={isTruckCompaniesLoading}
                headerRightComponent={
                    <IconButton
                        iconName="addCircle"
                        onClick={() => {
                            setSelectedTruckCompany(null);
                            setIsEditMode(false);
                            setShowEditModal(true);
                        }}
                        title={capitalizeFirstLetter(t('addTruckCompany'))}
                    />
                }
                exportCsvFileName="admin-truckscompanies"
                storageKey={localStorageKeys.TABLE.ADMIN.EXTERNAL.TRUCKCOMPANIES}
                agGridProps={{ getRowId: ({ data }) => data._id }}
            />

            {showEditModal && (
                <UpdateTruckCompanyModal
                    showEditModal={showEditModal}
                    setShowEditModal={setShowEditModal}
                    truckCompany={selectedTruckCompany}
                    isEditMode={isEditMode}
                    refetch={() => refetchTruckCompanies()}
                />
            )}
            {showDeleteModal && selectedTruckCompany && (
                <DeleteModal
                    open={showDeleteModal}
                    handleClose={() => setShowDeleteModal(false)}
                    deleteAction={() => {
                        mutateTruckCompany({ truckCompanyId: selectedTruckCompany._id, data: { crudStatus: crudStatusEnum.DELETED } });
                    }}
                    title={t('truckCompany')}
                    itemName={selectedTruckCompany.designation}
                />
            )}
        </>
    );
};

export default TrucksCompaniesSettingsRow;
